import * as React from "react";
import Typography from "@mui/material/Typography";
import { Button } from "@ssce/ui-shared-library";
import Region from "../Regions";
import { useNavigate } from "react-router-dom";
import Carousel from "../../subcomponents/Carousel";
import CardView from "../CardView";
import NorthAmerica from "../../../../assets/NorthAmerica.png";
import CatalogBenefits from "../CatalogBenefits";
import BoltIcon from "@mui/icons-material/Bolt";
import SpeedIcon from "@mui/icons-material/Speed";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import CatalogKeyFeatures from "../CatalogKeyFeatures";
import ChorusEverywherePricing from "./ChorusEverywherePricing";
import CatalogSubHeading from "../CatalogSubHeading";
import Grid from "@mui/material/Grid";
import Chorus01 from "../../../../assets/Chorus/Chorus01.png";
import Chorus02 from "../../../../assets/Chorus/Chorus02.png";
import Chorus03 from "../../../../assets/Chorus/Chorus03.png";
import Chorus04 from "../../../../assets/Chorus/Chorus04.png";

const CARD_DATA = [
  {
    id: 1,
    logo: "/api/assets/datamesh.png",
    serviceName: "SS&C Everywhere Data Mesh",
    minorVersion: 0,
    majorVersion: 1,
    shortDescription:
      "The SS&C Everywhere Data Mesh platform provides an interface for users to centralize their data to make it more accessible to the rapidly changing needs of a user’s organization.",
    tags: {
      function: "Cloud Platform",
      industry: "Cross-Industry",
      serviceType: "Data Service",
    },
  },
  {
    id: 2,
    logo: "/api/assets/algoriskreport.png",
    serviceName: "SS&C Algorithmics Risk Reports",
    minorVersion: 0,
    majorVersion: 1,
    shortDescription:
      "Generate world-class risk reports leveraging award and access powerful reporting dashboards preintegrated with your data.",
    tags: {
      function: "Risk Management",
      industry: "Cross-Industry",
      serviceType: "Analytics Service",
    },
  },
];

const ChorusEverywhereOverView: React.FC = () => {
  const navigate = useNavigate();

  const handleNavigateTo = (name: string) => {
    if (name === "SS&C Everywhere Data Mesh") {
      return navigate("/public-catalog/data-mesh");
    }
    if (name === "SS&C Algorithmics Risk Reports") {
      return navigate("/public-catalog/ss&c-algorithmics-risk-reports");
    }
  };

  return (
    <>
      <div className="flex-col gap3rem">
        <div className="flex-col gap1rem">
          <Typography
            variant="h4"
            fontSize="2.125rem"
            fontWeight={600}
            component="div"
            sx={{
              color: "#0A85C7",
              lineHeight: "2.25rem",
              padding: "0.625rem 0",
            }}
          >
            Overview
          </Typography>
          <div>
            <Typography
              variant="h6"
              fontSize="1.25rem"
              sx={{ color: "#02273C", lineHeight: "1.5rem" }}
            >
              About This Service
            </Typography>
            <Typography
              variant="body1"
              component={"div"}
              sx={{ color: "#000", lineHeight: "1.5rem" }}
            >
              <div style={{ display: "flex", maxWidth: "90%" }}>
                <ul>
                  <li>
                    {" "}
                    Chorus Everywhere leverages the data from Chorus to provide
                    a comprehensive reporting solution, integrating seamlessly
                    with SS&C's data mesh and cloud platform.{" "}
                  </li>
                  <li>
                    {" "}
                    This offering allows businesses to create and manage
                    business intelligence dashboards that deliver insights from
                    their Chorus data.{" "}
                  </li>
                  <li>
                    {" "}
                    Chorus includes tools for managing cases across multiple
                    channels, ensuring seamless communication and better
                    customer service.{" "}
                  </li>
                  <li>
                    {" "}
                    The AI-driven orchestration engine connects people,
                    processes, data, and systems, delivering the right
                    information to the right resource at the right time.{" "}
                  </li>
                  <li>
                    {" "}
                    Real-time insights and customizable dashboards provide
                    visibility into operations, helping organizations track
                    performance, prioritize tasks, and meet regulatory
                    requirements.{" "}
                  </li>
                </ul>
              </div>
            </Typography>

            <Carousel
              imageList={[
                {
                  src: Chorus01,
                  alt: "Chorus01",
                },
                {
                  src: Chorus02,
                  alt: "Chorus02",
                },
                {
                  src: Chorus03,
                  alt: "Chorus03",
                },
                {
                  src: Chorus04,
                  alt: "Chorus04",
                },
              ]}
            />
          </div>
        </div>
        <CatalogBenefits
          listItems={[
            {
              icon: (
                <SpeedIcon
                  color="primary"
                  sx={{ width: "4.875rem", height: "4.875rem" }}
                />
              ),
              heading: "Enhanced Customer Journeys",
              description:
                "By tailoring processes to meet specific customer needs and managing workflows from end-to-end, Chorus ensures a superior customer experience. This customization improves satisfaction and loyalty.",
            },

            {
              icon: (
                <BoltIcon
                  color="primary"
                  sx={{ width: "4.875rem", height: "4.875rem" }}
                />
              ),
              heading: "Accelerated Processing",
              description:
                "Chorus automates work and decisions, delivering tasks to the right resources quickly and integrating applications smoothly. This leads to faster processing times and increased operational efficiency.",
            },
            {
              icon: (
                <TrendingUpIcon
                  color="primary"
                  sx={{ width: "4.875rem", height: "4.875rem" }}
                />
              ),
              heading: "Increased Productivity",
              description:
                "Well-informed decision-making and quicker issue resolution are achieved through real-time operational insights and streamlined workflows. This boosts overall productivity and organizational effectiveness.",
            },
            {
              icon: (
                <QueryStatsIcon
                  color="primary"
                  sx={{ width: "4.875rem", height: "4.875rem" }}
                />
              ),
              heading: "Reduced Operational Risk",
              description:
                "Enforcing compliance and automatically checking quality helps mitigate operational risks. Continuous monitoring of all work ensures adherence to standards and reduces the likelihood of errors.",
            },
          ]}
        />
        <CatalogKeyFeatures
          childCount={2}
          listItems={[
            {
              heading: "Work Orchestration",
              description:
                "AI-powered process orchestration connects all business components, enabling efficient task distribution and automated decision-making at an enterprise scale.",
            },
            {
              heading: "Content Management",
              description:
                "Securely captures, stores, and makes available diverse content types, integrating them seamlessly into business processes for better workflow management.",
            },
            {
              heading: "Rules Management",
              description:
                "Automates business rules, allowing organizations to define, deploy, and execute rules that guide decisions and actions within processes, reducing manual intervention.",
            },
            {
              heading: "Role-Based Workspaces",
              description:
                "Provides tailored access and task routing based on roles, enhancing collaboration and efficiency across human and digital workers, customers, and suppliers.",
            },
          ]}
        />
        <ChorusEverywherePricing title={"Available Pricing Plans"} />
        <div className={"flex-col gap3rem"}>
          <CatalogSubHeading title={"Region Availability"} />
          <div className={"flex-col gap1rem"}>
            <CatalogSubHeading
              title={"Available Regions"}
              fontSize="1.25rem"
              color="#02273C"
            />
            <div>
              <Grid container spacing={0}>
                <Grid item xs={6} md={6}>
                  <Grid container spacing={0}>
                    <Grid item xs={6} md={6}>
                      <li className="regionListli">{"North America"}</li>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={6} md={6}>
                  <img
                    alt={"northAmerica"}
                    width={400}
                    height={200}
                    src={NorthAmerica}
                  />
                </Grid>
              </Grid>
            </div>
          </div>
          <div className="specificRegionDetils">
            <CatalogSubHeading
              title={"Specific Region Details"}
              fontSize="1.25rem"
              color="#02273C"
            />

            <div>
              <Grid container spacing={2}>
                <Grid item xs={4} md={4}>
                  <Region />
                </Grid>
                <Grid item xs={4} md={4}>
                  <Typography
                    variant="body1"
                    fontSize="1rem"
                    fontWeight={700}
                    component="div"
                    sx={{
                      color: "#02273C",
                      lineHeight: "1.5rem",
                      paddingBottom: 1,
                    }}
                  >
                    Region Details
                  </Typography>
                  <Typography
                    variant="body1"
                    fontSize="1rem"
                    fontWeight={400}
                    component="div"
                    sx={{ color: "#577687", lineHeight: "1.5rem" }}
                  >
                    North America: This region provides low-latency access to
                    customers located in the United States and Canada. It offers
                    high availability and scalability, making it an ideal choice
                    for enterprise customers with large-scale workloads.
                  </Typography>
                </Grid>
                <Grid item xs={4} md={4}>
                  <Typography
                    variant="body1"
                    fontSize="1rem"
                    fontWeight={700}
                    component="div"
                    sx={{
                      color: "#02273C",
                      lineHeight: "1.5rem",
                      paddingBottom: 1,
                    }}
                  >
                    Local Compliance
                  </Typography>
                  <Typography
                    variant="body1"
                    fontSize="1rem"
                    fontWeight={400}
                    component="div"
                    sx={{ color: "#577687", lineHeight: "1.5rem" }}
                  >
                    Fully compliant with regulatory requirements, including:
                  </Typography>
                  <Typography
                    variant="body1"
                    fontSize="1rem"
                    fontWeight={400}
                    component="div"
                    sx={{
                      color: "#577687",
                      lineHeight: "1.5rem",
                      paddingLeft: 2,
                    }}
                  >
                    <ul>
                      <li>GDPR</li>
                      <li>ISO 27000</li>
                      <li>SOC-2</li>
                    </ul>
                  </Typography>
                </Grid>
              </Grid>
            </div>
          </div>
        </div>
        <Typography
          variant="h4"
          fontSize="2.125rem"
          fontWeight={600}
          component="div"
          sx={{ color: "#0A85C7", lineHeight: "1.875rem" }}
        >
          Related Products
        </Typography>
        <div style={{ maxWidth: "80%" }}>
          <CardView
            variant="simple"
            data={CARD_DATA}
            handleNavigateTo={handleNavigateTo}
          />
          <Button
            variant="text"
            id={"viewMore"}
            onClick={() => {
              navigate("/public-catalog");
            }}
          >
            View More
          </Button>
        </div>
      </div>
    </>
  );
};

export default ChorusEverywhereOverView;
